import { normalizeContentCardForEditor } from './contentCardsUtils';
import type { ContentCardItem, ContentCardsSettings } from './types';

const CARD_DRAFT_KEY = 'gts.contentCard.draft.v1';
const BLOCK_DRAFT_KEY = 'gts.contentCards.blockDraft.v1';

export interface ContentCardsBlockDraft {
  title?: string;
  cards: ContentCardItem[];
  settings: ContentCardsSettings;
  savedAt: number;
}

const canUseStorage = (): boolean => typeof window !== 'undefined' && Boolean(window.localStorage);

const readJson = <T>(key: string): T | null => {
  if (!canUseStorage()) return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

const writeJson = (key: string, value: unknown): boolean => {
  if (!canUseStorage()) return false;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
};

export const cloneContentCard = (card: ContentCardItem): ContentCardItem =>
  normalizeContentCardForEditor({
    ...card,
    bulletItems: Array.isArray(card.bulletItems) ? [...card.bulletItems] : []
  });

export const saveContentCardDraft = (card: ContentCardItem): boolean =>
  writeJson(CARD_DRAFT_KEY, { ...cloneContentCard(card), savedAt: Date.now() });

export const loadContentCardDraft = (): ContentCardItem | null => {
  const draft = readJson<ContentCardItem & { savedAt?: number }>(CARD_DRAFT_KEY);
  if (!draft) return null;
  const { savedAt: _savedAt, ...card } = draft;
  return cloneContentCard(card);
};

export const hasContentCardDraft = (): boolean => loadContentCardDraft() != null;

export const saveContentCardsBlockDraft = (draft: {
  title?: string;
  cards: ContentCardItem[];
  settings: ContentCardsSettings;
}): boolean =>
  writeJson(BLOCK_DRAFT_KEY, {
    title: String(draft.title || '').trim() || undefined,
    cards: (draft.cards || []).map(cloneContentCard),
    settings: { ...draft.settings },
    savedAt: Date.now()
  } satisfies ContentCardsBlockDraft);

export const loadContentCardsBlockDraft = (): ContentCardsBlockDraft | null => {
  const draft = readJson<ContentCardsBlockDraft>(BLOCK_DRAFT_KEY);
  if (!draft || !Array.isArray(draft.cards) || draft.cards.length === 0) return null;
  return {
    title: String(draft.title || '').trim() || undefined,
    cards: draft.cards.map(cloneContentCard),
    settings: draft.settings,
    savedAt: Number(draft.savedAt) || Date.now()
  };
};

export const hasContentCardsBlockDraft = (): boolean => loadContentCardsBlockDraft() != null;
